import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import GoBackButton from '../../components/GoBackButton';
import StepRoleAddress from './components/StepRoleAddress';
import StepPropertyBasics from './components/StepPropertyBasics';
import StepPhotoUpload from './components/StepPhotoUpload';
import StepStoryQuestions from './components/StepStoryQuestions';
import StepGenerateCopy from './components/StepGenerateCopy';
import StepPriceGuidance from './components/StepPriceGuidance';
import StepProfessionalServices from './components/StepProfessionalServices';
import StepReviewPublish from './components/StepReviewPublish';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const CreateListingWizard = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [listingData, setListingData] = useState({
    role: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    type: 'house',
    beds: 3,
    baths: 2,
    parking: 2,
    listingType: 'buy',
    photos: [],
    storyAnswers: {
      whatYouLove: '',
      renovations: '',
      idealBuyer: ''
    },
    generatedCopy: {
      headlines: [],
      shortDescription: '',
      fullDescription: ''
    },
    price: '',
    professionalServices: []
  });

  // Auth guard - redirect if not logged in
  React.useEffect(() => {
    if (!authLoading && !user) {
      navigate('/authentication?redirect=/create-listing');
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) return null;

  const steps = [
    { number: 1, title: t('stepRoleAddress'), component: StepRoleAddress },
    { number: 2, title: t('stepPropertyBasics'), component: StepPropertyBasics },
    { number: 3, title: t('stepUploadPhotos'), component: StepPhotoUpload },
    { number: 4, title: t('stepTellStory'), component: StepStoryQuestions },
    { number: 5, title: t('stepGenerateCopy'), component: StepGenerateCopy },
    { number: 6, title: t('stepPriceGuidance'), component: StepPriceGuidance },
    { number: 7, title: t('stepProfessionalServices'), component: StepProfessionalServices },
    { number: 8, title: t('stepReviewPublish'), component: StepReviewPublish }
  ];

  const CurrentStepComponent = steps?.[currentStep - 1]?.component;

  const handleNext = (stepData) => {
    setListingData({ ...listingData, ...stepData });
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/authentication');
  };

  const headerUser = user ? {
    id: user?.id,
    name: userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email,
    role: userProfile?.role || 'owner'
  } : null;

  return (
    <>
      <Helmet>
        <title>Create Listing - ListingLens</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header user={headerUser} onLogout={handleLogout} />
        
        <main className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-8">
            <div className="mb-3">
              <GoBackButton fallbackPath="/agent-dashboard" label={t('goBack')} />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">{t('createYourListing')}</h1>
            <p className="text-muted-foreground">{t('createListingDesc')}</p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps?.map((step, index) => (
                <React.Fragment key={step?.number}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                        currentStep === step?.number
                          ? 'bg-primary text-primary-foreground'
                          : currentStep > step?.number
                          ? 'bg-success text-success-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {currentStep > step?.number ? (
                        <Icon name="Check" size={20} color="currentColor" />
                      ) : (
                        step?.number
                      )}
                    </div>
                    <p className="text-xs mt-2 text-center hidden md:block">{step?.title}</p>
                  </div>
                  {index < steps?.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded-full transition-all ${
                        currentStep > step?.number ? 'bg-success' : 'bg-muted'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-card rounded-xl border border-border p-6 md:p-8">
            {CurrentStepComponent && (
              <CurrentStepComponent
                data={listingData}
                onNext={handleNext}
                onBack={handleBack}
                isFirstStep={currentStep === 1}
                isLastStep={currentStep === steps?.length}
                userId={user?.id}
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateListingWizard;